import { ChevronDown, ChevronUp } from "lucide-react";
import { useRef, useState } from "react";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { cn } from "@/app/utils";

interface DateSpinnerProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
}

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export function DateSpinner({ value, onChange, placeholder, className, id }: DateSpinnerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [cursorPos, setCursorPos] = useState(0);

  const isValidDate = (dateString: string) => {
    if (!dateString) return true;
    if (dateString.length !== 10) return false;
    if (!ISO_DATE_REGEX.test(dateString)) return false;

    try {
      const [year, month, day] = dateString.split("-").map(Number) as [number, number, number];
      const date = new Date(year, month - 1, day);
      return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
    } catch {
      return false;
    }
  };

  const hasError = !isValidDate(value ?? "");

  const toISODateFormat = (date: Date) => date.toLocaleDateString("sv").slice(0, 10);
  const fromISODateFormat = (dateString: string) => {
    const [year, month, day] = dateString.split("-").map(Number) as [number, number, number];
    return new Date(year, month - 1, day);
  };

  const adjustDateAtCursor = (currentValue: string, position: number, increment: number) => {
    if (!currentValue || !ISO_DATE_REGEX.test(currentValue)) {
      return currentValue ?? "";
    }

    try {
      const date = fromISODateFormat(currentValue);
      const mutatedDate = new Date(date.getTime());

      if (position <= 3) {
        mutatedDate.setFullYear(mutatedDate.getFullYear() + increment);
      } else if (position >= 4 && position <= 6) {
        mutatedDate.setMonth(mutatedDate.getMonth() + increment);
      } else {
        mutatedDate.setDate(mutatedDate.getDate() + increment);
      }

      return toISODateFormat(mutatedDate);
    } catch {
      return currentValue;
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  const handleCursorChange = () => {
    if (inputRef.current) {
      setCursorPos(inputRef.current.selectionStart ?? 0);
    }
  };

  const handleSpinnerClick = (increment: number) => {
    if (!inputRef.current) return;
    if (!isValidDate(value ?? "")) return;

    const currentCursorPos = inputRef.current.selectionStart ?? cursorPos;
    const newValue = adjustDateAtCursor(value ?? "", currentCursorPos, increment);

    if (newValue !== value) {
      onChange(newValue);

      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(currentCursorPos, currentCursorPos);
          inputRef.current.focus();
        }
      }, 0);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();

      if (isValidDate(value ?? "")) {
        handleSpinnerClick(event.key === "ArrowUp" ? 1 : -1);
      }
    }
  };

  return (
    <div className={cn("relative flex items-center", className)}>
      <Input
        ref={inputRef}
        id={id}
        type="text"
        value={value ?? ""}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onSelect={handleCursorChange}
        onClick={handleCursorChange}
        placeholder={placeholder}
        className={cn(
          "text-sm font-mono pr-8",
          hasError && "border-red-500 focus-visible:ring-red-500",
        )}
      />
      <div className="absolute right-1 flex flex-col">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={hasError}
          className={cn(
            "h-4 w-6 p-0 hover:bg-stone-100",
            hasError && "opacity-50 cursor-not-allowed",
          )}
          onClick={() => handleSpinnerClick(1)}
        >
          <ChevronUp className="h-3 w-3" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={hasError}
          className={cn(
            "h-4 w-6 p-0 hover:bg-stone-100",
            hasError && "opacity-50 cursor-not-allowed",
          )}
          onClick={() => handleSpinnerClick(-1)}
        >
          <ChevronDown className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}
